import { useEffect, useState } from 'react';
import { fetchSettings, saveSettings, type SettingsView } from '../lib/api';
import { useActionError } from '../lib/action-error';
import { ask } from '../lib/ask';
import { InfoTip } from '../lib/info-tip';
import { labels } from '../lib/labels';
import { RoleNote, useGate } from '../lib/session';
import { ApiClients } from './ApiClients';

type LoadState = { status: 'loading' } | { status: 'error' } | { status: 'ok'; view: SettingsView };

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
/** The largest duration the backend takes. Mirrors `MAX_DURATION_MS` in
 *  `backend/src/settings/console-settings.service.ts`; keep the two equal. */
const MAX_DURATION_MS = 365 * DAY_MS;

/** `msToUnit`/`unitToMs`: the API is milliseconds throughout (`backend/src/settings/view.ts`'s own
 *  header — "the frontend owns converting to/from the mockup's per-field unit"); the mockup shows
 *  days for the two big bounds, minutes for the pause grace period. */
function msToUnit(ms: number | null, unitMs: number): string {
  // 4 decimal places keep 0.25 hours and 0.01 days whole; `+` drops the trailing zeros.
  return ms === null ? '' : String(+(ms / unitMs).toFixed(4));
}

/** An empty field is `null` (not set). A number from 1 ms up to the maximum is milliseconds. A
 *  value over the maximum is `'tooLarge'`. Anything else, a negative, `0`, or a value that is
 *  under 1 ms, is `undefined` (invalid): the form blocks both before the request, and the
 *  backend's rules are the second check. */
function unitToMs(value: string, unitMs: number): number | null | undefined | 'tooLarge' {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const ms = Math.round(Number(trimmed) * unitMs);
  if (ms > MAX_DURATION_MS) return 'tooLarge';
  return Number.isFinite(ms) && ms > 0 ? ms : undefined;
}

interface FormState {
  humanReviewDays: string;
  deadLetterHours: string;
  spotCheckDays: string;
  stalledHours: string;
  burstCount: string;
  burstWindowMinutes: string;
  recipients: string;
  pauseGraceMinutes: string;
}

function toForm(view: SettingsView): FormState {
  return {
    humanReviewDays: msToUnit(view.bounds.humanReviewMs, DAY_MS),
    deadLetterHours: msToUnit(view.bounds.deadLetterMs, HOUR_MS),
    spotCheckDays: msToUnit(view.bounds.spotCheckMs, DAY_MS),
    stalledHours: msToUnit(view.bounds.stalledMs ?? null, HOUR_MS),
    burstCount: msToUnit(view.bounds.deadLetterBurstCount ?? null, 1),
    burstWindowMinutes: msToUnit(view.bounds.deadLetterBurstWindowMs ?? null, MINUTE_MS),
    recipients: view.recipients.join('; '),
    pauseGraceMinutes: msToUnit(view.pauseGraceMs, MINUTE_MS),
  };
}

/** The backend save is a patch (a missing key keeps its value, `null` means not set). This form
 *  still sends every field, so a save always states the whole form. A field the
 *  operator did not touch keeps its saved milliseconds rather than a round trip through the
 *  4-decimal display text. */
function toApiInput(form: FormState, original: FormState, view: SettingsView) {
  const ms = (key: keyof FormState, unitMs: number, saved: number | null) =>
    (form[key] === original[key] ? saved : (unitToMs(form[key], unitMs) as number | null | undefined)) ?? null;
  return {
    humanReviewMs: ms('humanReviewDays', DAY_MS, view.bounds.humanReviewMs),
    deadLetterMs: ms('deadLetterHours', HOUR_MS, view.bounds.deadLetterMs),
    spotCheckMs: ms('spotCheckDays', DAY_MS, view.bounds.spotCheckMs),
    stalledMs: ms('stalledHours', HOUR_MS, view.bounds.stalledMs ?? null),
    deadLetterBurstCount: ms('burstCount', 1, view.bounds.deadLetterBurstCount ?? null),
    deadLetterBurstWindowMs: ms('burstWindowMinutes', MINUTE_MS, view.bounds.deadLetterBurstWindowMs ?? null),
    recipients: form.recipients
      .split(';')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0),
    pauseGraceMs: ms('pauseGraceMinutes', MINUTE_MS, view.pauseGraceMs),
  };
}

const UNIT_MS: Partial<Record<keyof FormState, number>> = {
  humanReviewDays: DAY_MS,
  deadLetterHours: HOUR_MS,
  spotCheckDays: DAY_MS,
  stalledHours: HOUR_MS,
  burstCount: 1,
  burstWindowMinutes: MINUTE_MS,
  pauseGraceMinutes: MINUTE_MS,
};

const UNIT_LABEL: Partial<Record<keyof FormState, string>> = {
  humanReviewDays: labels.settings.unitDays,
  deadLetterHours: labels.settings.unitHours,
  spotCheckDays: labels.settings.unitDays,
  stalledHours: labels.settings.unitHours,
  burstCount: labels.settings.unitDocuments,
  burstWindowMinutes: labels.settings.unitMinutes,
  pauseGraceMinutes: labels.settings.unitMinutes,
};

/** The inline message for a field the form must not save, or `null` when the field is fine. */
function fieldError(form: FormState, key: keyof FormState): string | null {
  const unitMs = UNIT_MS[key];
  if (unitMs === undefined) return null;
  const ms = unitToMs(form[key], unitMs);
  if (ms === undefined) return labels.settings.invalidNumber;
  if (ms === 'tooLarge') return labels.settings.tooLarge(MAX_DURATION_MS / unitMs, UNIT_LABEL[key]!);
  return null;
}

function countChanges(current: FormState, original: FormState): number {
  return (Object.keys(current) as Array<keyof FormState>).filter((key) => current[key] !== original[key]).length;
}

/** `#settings`: the three waiting bounds, the alert recipient
 *  list, and the pause grace period, all on `config/console.json`. One guarded write, `Save`,
 *  naming how many values changed. */
export function Settings() {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [original, setOriginal] = useState<FormState | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saved, setSaved] = useState(false);
  // A number field the browser cannot parse (`-`, `1e`) reads as `''`; remember it as invalid.
  const [badInput, setBadInput] = useState<Set<keyof FormState>>(new Set());
  const action = useActionError();
  const gate = useGate('administer');

  function reload() {
    setState({ status: 'loading' });
    setSaved(false);
    setBadInput(new Set());
    fetchSettings()
      .then((view) => {
        const asForm = toForm(view);
        setState({ status: 'ok', view });
        setOriginal(asForm);
        setForm(asForm);
      })
      .catch(() => setState({ status: 'error' }));
  }

  useEffect(reload, []);

  if (state.status === 'loading') {
    return (
      <div>
        <div className="page-head">
          <h2>{labels.pageTitles.settings}</h2>
        </div>
        <p className="notice">{labels.settings.loading}</p>
      </div>
    );
  }
  if (state.status === 'error' || !form || !original) {
    return (
      <div>
        <div className="page-head">
          <h2>{labels.pageTitles.settings}</h2>
        </div>
        <p className="notice notice--crit" role="alert">
          {labels.settings.loadFailed}
        </p>
      </div>
    );
  }

  const changeCount = countChanges(form, original);
  const error = (key: keyof FormState) => (badInput.has(key) ? labels.settings.invalidNumber : fieldError(form, key));
  const hasInvalid = (Object.keys(form) as Array<keyof FormState>).some((key) => error(key) !== null);

  function set<K extends keyof FormState>(key: K, value: string, bad = false) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setBadInput((prev) => (bad ? new Set(prev).add(key) : new Set([...prev].filter((k) => k !== key))));
    setSaved(false);
  }

  async function handleSave() {
    if (changeCount === 0) return;
    const answers = await ask({
      ...labels.settings.saveConfirm(changeCount),
      confirmLabel: labels.settings.saveButton(changeCount),
    });
    if (!answers) return;
    let result: Awaited<ReturnType<typeof saveSettings>> | undefined;
    const view = state.status === 'ok' ? state.view : null;
    if (!view || !(await action.run(async () => (result = await saveSettings(toApiInput(form!, original!, view)))))) {
      return;
    }
    setState({ status: 'ok', view: result!.view });
    const asForm = toForm(result!.view);
    setOriginal(asForm);
    setForm(asForm);
    setBadInput(new Set());
    setSaved(true);
  }

  return (
    <div>
      <div className="page-head">
        <h2>{labels.pageTitles.settings}</h2>
      </div>

      {/* QA-79: one panel, as wide as its fields, and Save is inside it: it saves everything above it. */}
      <section className="panel panel--fields">
        <div className="panel__head">
          <h3>{labels.settings.waitHeading}</h3>
        </div>
        <BoundRow
          label={labels.settings.humanReview}
          tip={labels.settings.waitLimitTip}
          unit={labels.settings.unitDays}
          value={form.humanReviewDays}
          error={error('humanReviewDays')}
          disabled={!gate.allowed}
          onChange={(value, bad) => set('humanReviewDays', value, bad)}
        />
        <BoundRow
          label={labels.settings.deadLetter}
          unit={labels.settings.unitHours}
          value={form.deadLetterHours}
          error={error('deadLetterHours')}
          disabled={!gate.allowed}
          onChange={(value, bad) => set('deadLetterHours', value, bad)}
        />
        <BoundRow
          label={labels.settings.spotCheck}
          tip={labels.settings.waitLimitTip}
          unit={labels.settings.unitDays}
          value={form.spotCheckDays}
          error={error('spotCheckDays')}
          disabled={!gate.allowed}
          onChange={(value, bad) => set('spotCheckDays', value, bad)}
        />
        <BoundRow
          label={labels.settings.stalled}
          unit={labels.settings.unitHours}
          value={form.stalledHours}
          error={error('stalledHours')}
          disabled={!gate.allowed}
          onChange={(value, bad) => set('stalledHours', value, bad)}
        />
        <BoundRow
          label={labels.settings.burstCount}
          unit={labels.settings.unitDocuments}
          value={form.burstCount}
          error={error('burstCount')}
          disabled={!gate.allowed}
          onChange={(value, bad) => set('burstCount', value, bad)}
        />
        <BoundRow
          label={labels.settings.burstWindow}
          unit={labels.settings.unitMinutes}
          value={form.burstWindowMinutes}
          error={error('burstWindowMinutes')}
          disabled={!gate.allowed}
          onChange={(value, bad) => set('burstWindowMinutes', value, bad)}
        />
        <p className="panel__sub flow-md">{labels.settings.waitHelp}</p>

        <div className="subpanel">
          <div className="panel__head">
            <h3>{labels.settings.whoHeading}</h3>
          </div>
          {/* The recipient list is a free-text field rather than a number, so it takes the full
            row: at 320px a label and a semicolon-separated address list cannot share one. */}
          <p className="field field--wide">
            <span className="field__label">{labels.settings.recipients}</span>
            <span className="field__control">
              <input
                type="text"
                aria-label={labels.settings.recipients}
                value={form.recipients}
                disabled={!gate.allowed}
                onChange={(event) => set('recipients', event.target.value)}
                placeholder={labels.settings.notSet}
              />
            </span>
          </p>
          <p className="field">
            <span className="field__label">{labels.settings.pauseGrace}</span>
            <span className="field__control">
              <input
                type="number"
                step="any"
                aria-label={labels.settings.pauseGrace}
                aria-invalid={error('pauseGraceMinutes') !== null || undefined}
                value={form.pauseGraceMinutes}
                disabled={!gate.allowed}
                onChange={(event) => set('pauseGraceMinutes', event.target.value, event.target.validity.badInput)}
                placeholder={labels.settings.notSet}
              />
              <span className="field__unit">{labels.settings.unitMinutes}</span>
            </span>
            {error('pauseGraceMinutes') && <FieldError message={error('pauseGraceMinutes')!} />}
          </p>
        </div>

        {/* The row sits at the end edge, so its note comes before the button it describes. */}
        <div className="action-row action-row--end subpanel">
          <RoleNote action="administer" />
          {gate.allowed && changeCount === 0 && !saved && <span className="action-row__note">{labels.settings.noChanges}</span>}
          {/* Settings is the one screen that reports a silent success out loud: the values it wrote
            look identical afterwards, so without this the operator cannot tell it landed. */}
          {saved && <span className="action-row__note">{labels.settings.saved}</span>}
          {/* Save is the one thing an operator opened this screen to do, so it is this view's single
            primary control — and it is guarded, naming how many values changed. */}
          <button
            type="button"
            className="btn btn--primary"
            {...gate.props}
            disabled={!gate.allowed || changeCount === 0 || hasInvalid}
            onClick={handleSave}
          >
            {labels.settings.saveButton(changeCount)}
          </button>
        </div>
        {action.notice}
      </section>

      {/* API clients are admin-only (ADR 0040). The component makes its request on mount, so it is not rendered for another role. */}
      {gate.allowed && <ApiClients />}
    </div>
  );
}

function BoundRow({
  label,
  tip,
  unit,
  value,
  error,
  disabled,
  onChange,
}: {
  label: string;
  /** QA-132: a short note shown in an ⓘ next to the label. */
  tip?: string;
  unit: string;
  value: string;
  error: string | null;
  disabled: boolean;
  onChange: (value: string, badInput: boolean) => void;
}) {
  return (
    <p className="field">
      <span className="field__label">
        {label}
        {tip && <InfoTip label={label} text={tip} />}
      </span>
      <span className="field__control">
        <input
          type="number"
          step="any"
          aria-label={label}
          aria-invalid={error !== null || undefined}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value, event.target.validity.badInput)}
          placeholder={labels.settings.notSet}
        />
        {/* The unit always shows, in a fixed-width slot so every input starts at the same x. The
            placeholder already says "not set" when the field is empty. */}
        <span className="field__unit">{unit}</span>
      </span>
      {error && <FieldError message={error} />}
    </p>
  );
}

function FieldError({ message }: { message: string }) {
  return (
    <span className="field__error" role="alert">
      {message}
    </span>
  );
}
